import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ScheduleService } from '../services/schedule.service';
import { AssignmentType } from '../model/assignmentType';
import { BusService } from '../services/bus.service';
import { Bus } from '../model/bus';
import { UserMapping } from '../model/usermapping';
import { UserMappingService } from '../services/usermapping.service';
import { List } from 'immutable';
import { DateTimeUtils } from '../shared/datetimeutils';
import { Schedule } from '../model/schedule';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { HttpErrorResponse } from '@angular/common/http';
import { BusMapConstants } from '../busmap/busmap.constants';
import { WorkScheduleValidator } from './work-schedule-validator';
import { BusWithOverlaps } from '../model/busWithOverlap';
import { combineLatest, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
@Component({
  selector: 'app-create-work-schedule',
  templateUrl: './create-work-schedule.component.html',
  styleUrls: ['./work-schedule.component.css']
})
export class CreateWorkScheduleComponent implements OnInit {
  public filteredUserMappings: Observable<UserMapping[]>;

  public form: UntypedFormGroup;
  public driver: string = "";
  public type: string = "";
  public bus: string = "";
  public from: string = "";
  public to: string = "";
  public note: string = "";
  public activityName: string = "";
  public buses: Bus[];
  public assignmentTypes: AssignmentType[];
  public userMappings: UserMapping[];
  public timeListFrom: string[];
  public timeListTo: string[];

  public bussesFormControl = new UntypedFormControl(this.bus);
  public typesFormControl = new UntypedFormControl(this.type, Validators.required);
  public driversFormControl = new UntypedFormControl(this.type, Validators.required);
  public fromFormControl = new UntypedFormControl(this.from, Validators.required);
  public toFormControl = new UntypedFormControl(this.to, Validators.required);

  private readonly airportId: string;
  private readonly activeDate: string;
  private readonly noBusSelected: string = "Ingen buss";

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly data: any,
    private readonly fb: UntypedFormBuilder,
    private readonly scheduleService: ScheduleService,
    private readonly busService: BusService,
    private readonly userMappingService: UserMappingService,
    private readonly dialogRef: MatDialogRef<CreateWorkScheduleComponent>,
    private readonly toastr: ToastrService) {

    this.assignmentTypes = [];
    this.buses = [];
    this.timeListFrom = DateTimeUtils.getTimeArrayQuartersFrom();
    this.timeListTo = DateTimeUtils.getTimeArrayQuartersTo();

    this.airportId = data.airportId;
    this.activeDate = data.activeDate;
  }

  public ngOnInit(): void {
    this.scheduleService.getAssignmentTypes().then((assignmentTypes: AssignmentType[]) => {
      this.assignmentTypes = assignmentTypes;
    });

    this.userMappingService.UserMappings.subscribe((userMappings: List<UserMapping>) => {
      this.userMappings = userMappings.sort(
        (a, b) => a.name.localeCompare(b.name)).toArray();
    });

    this.getAvailableBuses();

    this.form = this.fb.group({
      driver: this.driversFormControl,
      type: this.typesFormControl,
      bussesFc: this.bussesFormControl,
      from: this.fromFormControl,
      to: this.toFormControl,
      note: new UntypedFormControl(this.note),
      activityName: new UntypedFormControl(this.activityName),
      activeDate: this.activeDate
    },
      { validator: WorkScheduleValidator.timeRangeValidator });

    this.typesFormControl.valueChanges.subscribe((value: any) => {
      this.driversFormControl.setValue('');

      if (value.name.toLocaleUpperCase() === 'KOOR' || value.name.toLocaleUpperCase() === 'OTHER') {
        this.bussesFormControl.clearValidators();
        this.bussesFormControl.reset();        
      } 
      this.getAvailableBuses();
      
    });

    this.filteredUserMappings = combineLatest([
      this.driversFormControl.valueChanges.pipe(startWith<string | UserMapping>('')),
      this.typesFormControl.valueChanges.pipe(startWith<any>(this.typesFormControl.value)),
    ]).pipe(
      map(([driverValue, assignmentType]) => {
        const nameFilter = (typeof driverValue === 'string' ? driverValue : driverValue?.name ?? '').toLowerCase();
        const typeName = (assignmentType?.name ?? '').toString();

        return this.userMappings
          .filter(u => u.name?.toLowerCase().startsWith(nameFilter))
          .filter(u => this.userHasSkill(u, typeName));
      })
    );

  }

  private userHasSkill(userMapping: UserMapping, requiredSkill: string): boolean {
    const required = (requiredSkill ?? '').toUpperCase();
    if (!required) return true;

    if (required !== "AIR" && required !== "LAND") {
      return true;
    }

    const raw = userMapping?.skills;
    if (!raw) return false;

    try {
      const skillsArray = (JSON.parse(raw) as string[]).map(s => (s ?? '').toUpperCase());
      const skills = new Set(skillsArray);

      if (required === "LAND") {
        return skills.has("LAND") || skills.has("AIR");
      }

      return skills.has(required);
    } catch {
      return false;
    }
  }

  public displayFn(userMapping?: UserMapping): string | undefined {
    return userMapping ? userMapping.name : undefined;
  }

  public getDriverBackgroundColor(userMapping: UserMapping): string {
    const skills = (userMapping?.skills ?? "").toUpperCase();

    if (skills.includes("AIR")) {
      return "#C6E2EF";
    }

    if (skills.includes("LAND")) {
      return "#DFF0D8"; 
    }

    return "transparent";
  }


  public submit() {
    const schedule = new Schedule();
    schedule.buss = this.bussesFormControl.value?.number == this.noBusSelected ? null : (this.bussesFormControl.value?.number ?? "Okänd");
    schedule.comment = this.form.controls['note'].value;
    schedule.name = this.driversFormControl.value.name;
    schedule.userMappingId = this.driversFormControl.value.id;
    schedule.skill = "[\"" + this.typesFormControl.value.name + "\"]";
    schedule.activityName = this.form.controls['activityName'].value;
    schedule.airportId = this.airportId;

    //start
  
    schedule.start = this.getFromValue();

    //end
    schedule.end = this.getToValue();

    this.scheduleService.addSchedule(schedule)
      .then((createdSchedule: Schedule) => {
        this.dialogRef.close(createdSchedule);
        this.toastr.success("Schema skapat för " + schedule.name);
      })
      .catch((err: HttpErrorResponse) => {
        this.toastr.error(err.error, "Error");
      });
  }

  public canSave() {
    return !this.form.invalid;
  }

  public toChanged(event) {
    this.getAvailableBuses();
  }

  private _filter(value: string): UserMapping[] {
    const filterValue = value.toLowerCase();

    return this.userMappings.filter(usermapping => usermapping.name.toLowerCase().startsWith(filterValue));

  }

  private getFromValue() : Date {
    let f = this.fromFormControl.value as string;
    if (f === "") return null;

    return DateTimeUtils.convertStringToDateFrom(this.activeDate, f);
  }

  private getToValue(): Date {
    let f = this.toFormControl.value as string;
    if (f === "") return null;

    let date = DateTimeUtils.convertStringToDateTo(this.activeDate, f);
    return date;
  }

  private getAvailableBuses(): void {
    let start = this.getFromValue();
    let end = this.getToValue();

    if (start === null || end === null) return;

    this.busService.getAvailableBuses(null, this.getFromValue(), this.getToValue()).then((buses: BusWithOverlaps[]) => {
      if (this.typesFormControl.value.name === undefined || this.typesFormControl.value.name.toLocaleUpperCase() == 'KOOR' || this.typesFormControl.value.name.toLocaleUpperCase() == 'OTHER') {
        this.bussesFormControl.setValue('');        
      }
      else {
        this.buses = [...buses];
        let bus = new BusWithOverlaps();
        bus.number = this.noBusSelected;
        this.buses.unshift(bus);

        this.buses.sort((b1: BusWithOverlaps, b2: BusWithOverlaps) => +b1.number - +b2.number);
      }
      this.bussesFormControl.updateValueAndValidity();
      
    });
  }
}
